import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Shield, AlertTriangle, Music, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Moderation() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUser = async () => {
    if (!session) {
      navigate("/Auth");
      return;
    }
    const { data: profile } = await digitalOcean
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setCurrentUser(profile);

    const { data: roles } = await digitalOcean
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id);
    
    const hasModAccess = roles?.some(r => r.role === 'admin' || r.role === 'moderator');
    setIsAdmin(hasModAccess);
  };

  const { data: reports } = useQuery({
    queryKey: ["reports"],
    queryFn: async () => {
      const { data, error } = await digitalOcean
        .from("reports")
        .select(`
          *,
          reporter:profiles!reports_reporter_id_fkey(username),
          reported_track:tracks(title, artist_name),
          reported_user:profiles!reports_reported_user_id_fkey(username)
        `)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: isAdmin,
  });

  const { data: genreSuggestions } = useQuery({
    queryKey: ["genre-suggestions"],
    queryFn: async () => {
      const { data, error } = await digitalOcean
        .from("genre_suggestions")
        .select(`
          *,
          user:profiles!genre_suggestions_user_id_fkey(username, artist_name)
        `)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: isAdmin,
  });

  const handleGenreStatusUpdate = async (suggestionId, newStatus) => {
    try {
      const { error } = await digitalOcean
        .from("genre_suggestions")
        .update({ status: newStatus })
        .eq("id", suggestionId);

      if (error) throw error;

      toast.success(`Genre suggestion ${newStatus === 'approved' ? 'approved' : 'rejected'}`);
      queryClient.invalidateQueries({ queryKey: ["genre-suggestions"] });
    } catch (error) {
      console.error("Error updating genre suggestion:", error);
      toast.error("Failed to update genre suggestion");
    }
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen p-8">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You need admin or moderator privileges to access this page.
          </p>
          <Button onClick={() => navigate("/Discover")} tooltip="Return to the discover page">Back to Discover</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
          <Shield className="w-8 h-8" />
          Moderation Dashboard
        </h1>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Pending Reports ({reports?.length || 0})
          </h2>

          {!reports || reports.length === 0 ? (
            <div className="bg-card p-8 rounded-lg border border-border text-center">
              <p className="text-muted-foreground">No pending reports</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <div key={report.id} className="bg-card p-6 rounded-lg border border-border">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-card-foreground mb-1">
                        {report.reported_track 
                          ? `Track: ${report.reported_track.title}` 
                          : `User: @${report.reported_user?.username}`}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Reported by {report.reporter?.username} • {new Date(report.created_at).toLocaleString()}
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-destructive/20 text-destructive rounded text-sm">
                      {report.status}
                    </span>
                  </div>
                  
                  <div className="bg-muted/50 p-4 rounded mb-4">
                    <p className="text-sm font-semibold text-foreground mb-1">Reason:</p>
                    <p className="text-card-foreground">{report.reason}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button size="sm" tooltip="Mark this report as reviewed">Review</Button>
                    <Button size="sm" variant="outline" tooltip="Dismiss this report without action">Dismiss</Button>
                    <Button size="sm" variant="destructive" tooltip="Take action on this report (ban user, remove track, etc.)">Take Action</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <Music className="w-5 h-5 text-primary" />
            Genre Suggestions ({genreSuggestions?.filter(g => g.status === 'pending').length || 0} pending)
          </h2>

          {!genreSuggestions || genreSuggestions.length === 0 ? (
            <div className="bg-card p-8 rounded-lg border border-border text-center">
              <p className="text-muted-foreground">No genre suggestions</p>
            </div>
          ) : (
            <div className="space-y-4">
              {genreSuggestions.map((suggestion) => (
                <div key={suggestion.id} className="bg-card p-6 rounded-lg border border-border">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-card-foreground mb-1 text-lg">
                        {suggestion.genre_name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        Suggested by {suggestion.user?.username || suggestion.user?.artist_name || 'Unknown'} • {new Date(suggestion.created_at).toLocaleString()}
                      </p>
                      {suggestion.description && (
                        <div className="bg-muted/50 p-4 rounded mt-2">
                          <p className="text-sm font-semibold text-foreground mb-1">Description:</p>
                          <p className="text-card-foreground text-sm">{suggestion.description}</p>
                        </div>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded text-sm ${
                      suggestion.status === 'approved' 
                        ? 'bg-green-500/20 text-green-500' 
                        : suggestion.status === 'rejected'
                        ? 'bg-destructive/20 text-destructive'
                        : 'bg-yellow-500/20 text-yellow-500'
                    }`}>
                      {suggestion.status}
                    </span>
                  </div>

                  {suggestion.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => handleGenreStatusUpdate(suggestion.id, 'approved')}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleGenreStatusUpdate(suggestion.id, 'rejected')}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
